import type { ReactNode } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  RotateCcw,
  SkipForward
} from 'lucide-react';
import { BRAND_ACTION_COLOR } from '@/constants/ui';

interface AudioTransportProps {
  isPlaying: boolean;
  progress: number;
  onToggle: () => void;
  variant?: 'clip' | 'slides';

  onRestart?: () => void;
  onSkipNext?: () => void;
  skipDisabled?: boolean;

  onPrev?: () => void;
  onNext?: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;

  onFinish?: () => void;
  isLast?: boolean;
  finishEnabled?: boolean;

  leftCaption?: ReactNode;
  rightCaption?: ReactNode;
}

const roundControlClasses =
  'flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:hover:bg-transparent';

export function AudioTransport({
  isPlaying,
  progress,
  onToggle,
  variant = 'clip',
  onRestart,
  onSkipNext,
  skipDisabled = false,
  onPrev,
  onNext,
  prevDisabled = false,
  nextDisabled = false,
  onFinish,
  isLast = false,
  finishEnabled = false,
  leftCaption,
  rightCaption
}: AudioTransportProps) {
  const isSlides = variant === 'slides';
  const showFinish = isSlides && isLast && Boolean(onFinish);

  return (
    <div className="w-full" data-testid="audio-transport">
      <div className="h-0.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className="h-full rounded-full transition-[width] duration-150 ease-linear"
          style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: BRAND_ACTION_COLOR }}
        />
      </div>

      <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <div className="min-w-0 truncate text-left text-sm text-slate-600">{leftCaption}</div>

        <div className="flex items-center justify-center gap-4">
          {isSlides ? (
            <button
              type="button"
              onClick={onPrev}
              disabled={prevDisabled}
              aria-label="Previous slide"
              className={roundControlClasses}
            >
              <ChevronLeft size={22} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onRestart}
              aria-label="Restart clip"
              className={roundControlClasses}
            >
              <RotateCcw size={18} />
            </button>
          )}

          <button
            type="button"
            onClick={onToggle}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition hover:brightness-110"
            style={{
              backgroundColor: BRAND_ACTION_COLOR,
              boxShadow: `0 10px 24px ${BRAND_ACTION_COLOR}4d`
            }}
          >
            {isPlaying ? (
              <Pause size={24} fill="currentColor" />
            ) : (
              <Play size={24} fill="currentColor" className="ml-0.5" />
            )}
          </button>

          {isSlides ? (
            <button
              type="button"
              onClick={onNext}
              disabled={nextDisabled}
              aria-label="Next slide"
              className={roundControlClasses}
            >
              <ChevronRight size={22} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSkipNext}
              disabled={skipDisabled}
              aria-label="Skip to next"
              className={roundControlClasses}
            >
              <SkipForward size={18} />
            </button>
          )}
        </div>

        <div className="flex min-w-0 items-center justify-end text-right text-sm text-slate-600">
          {showFinish ? (
            <button
              type="button"
              onClick={onFinish}
              disabled={!finishEnabled}
              aria-label="Finish module"
              className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ backgroundColor: BRAND_ACTION_COLOR }}
            >
              <Check size={16} /> Finish
            </button>
          ) : (
            <span className="truncate">{rightCaption}</span>
          )}
        </div>
      </div>
    </div>
  );
}
